import React, { useContext, useEffect, useMemo } from 'react';
import { uniqBy } from 'lodash';
import { some } from 'lodash/collection';
import { isEmpty, isEqual } from 'lodash/lang';

import InvoiceDatePicker from 'components/pages/finance/NewInvoice/CreateInvoice/ConfigurationSection/InvoiceDatePicker';

import { StudySiteApi } from '../../../../../../api';
import FinClientApi from '../../../../../../api/finance/FinClientApi';
import FinPayeeApi from '../../../../../../api/finance/FinPayeeApi';
import Input from '../../../../../../common/data-entry/Input';
import Select from '../../../../../../common/data-entry/Select';
import { onRequestError } from '../../../../../../services/handlers';
import { InvoiceContext } from '../../NewInvoiceContext';
import { CONTACT_NAMES, REMIT, TERMS } from '../CreateInvoiceConstants';
import { addSuffixToId, parseSerializedObject, payeeToString, transformedPayees } from '../CreateInvoiceServices';

export const ConfigurationSection = ({
  billToList,
  setBillToList,
  selectedBillTo,
  setSelectedBillTo,
  invoiceCreated,
  savingType,
  payees,
  setPayees,
  selectedPayee,
  setSelectedPayee,
  selectedTerms,
  setSelectedTerms,
  remit,
  setRemit,
  selectedContactName,
  setSelectedContactName,
  investigators,
  selectedInvestigator,
  setSelectedInvestigator,
  billToLocalStorage,
  payeeLocalStorage,
  mainLedgerEvents,
  fmsStudy,
  studySiteId,
  investigatorName,
  setInvestigators
}) => {
  const { createInvoiceItems, editPDFMode } = useContext(InvoiceContext);

  useEffect(() => {
    if (studySiteId) {
      StudySiteApi.getInvestigatorsBySsuId(studySiteId).then(res => {
        const uniqueInvestigators = uniqBy(res?.data, 'name');
        setInvestigators(
          uniqueInvestigators?.map(investigator => ({ name: investigator.name, id: investigator.name }))
        );
      });
    }
  }, [setInvestigators, studySiteId]);

  useEffect(() => {
    if (investigatorName) {
      setSelectedInvestigator({ name: investigatorName, id: investigatorName });
    } else {
      setSelectedInvestigator(null);
    }
  }, [investigatorName, setSelectedInvestigator]);

  useEffect(() => {
    if (
      isEmpty(createInvoiceItems.createdInvoiceDetails) &&
      isEmpty(createInvoiceItems.pdf) &&
      (!isEmpty(createInvoiceItems.originalLedgerEvents) ||
        !isEmpty(createInvoiceItems.withholdingLedgerEvents) ||
        createInvoiceItems.creditMemoLedgerEvents ||
        createInvoiceItems.debitMemoLedgerEvents) &&
      isEmpty(billToList)
    ) {
      FinClientApi.viewUsage().then(({ data }) => {
        const clientName = billToLocalStorage[mainLedgerEvents?.[0].ssuId]
          ? billToLocalStorage[mainLedgerEvents?.[0].ssuId]
          : mainLedgerEvents?.[0].clientName;
        const billToList = data.map(el => el.client);
        setBillToList(billToList.sort((a, b) => a.name.localeCompare(b.name)));
        setSelectedBillTo(billToList.find(el => el.name === clientName));
      });
    }
  }, [
    billToList,
    billToLocalStorage,
    createInvoiceItems.createdInvoiceDetails,
    createInvoiceItems.creditMemoLedgerEvents,
    createInvoiceItems.debitMemoLedgerEvents,
    createInvoiceItems.originalLedgerEvents,
    createInvoiceItems.pdf,
    createInvoiceItems.withholdingLedgerEvents,
    mainLedgerEvents,
    setBillToList,
    setSelectedBillTo
  ]);

  useEffect(() => {
    if (!isEmpty(createInvoiceItems.createdInvoiceDetails) && !isEmpty(createInvoiceItems.pdf)) {
      FinClientApi.viewUsage().then(({ data }) => {
        const billToList = data.map(el => el.client);
        const oldBillTo = parseSerializedObject(createInvoiceItems?.createdInvoiceDetails?.client);
        const isClientWasUpdated = !billToList.some(el => isEqual(el, oldBillTo));
        const newBillToList =
          isClientWasUpdated && !isEmpty(oldBillTo) ? [...billToList, addSuffixToId(oldBillTo)] : billToList;
        const newSelectedBillTo = newBillToList.find(
          el => el.id === (isClientWasUpdated ? oldBillTo?.id + 'old' : oldBillTo?.id)
        );
        setBillToList(newBillToList);
        setSelectedBillTo(newSelectedBillTo);
      });
    }
  }, [createInvoiceItems.createdInvoiceDetails, createInvoiceItems.pdf, setBillToList, setSelectedBillTo]);

  useEffect(() => {
    if (
      !editPDFMode &&
      isEmpty(createInvoiceItems.createdInvoiceDetails) &&
      isEmpty(createInvoiceItems.pdf) &&
      (!isEmpty(createInvoiceItems.originalLedgerEvents) ||
        !isEmpty(createInvoiceItems.withholdingLedgerEvents) ||
        createInvoiceItems.creditMemoLedgerEvents ||
        createInvoiceItems.debitMemoLedgerEvents)
    ) {
      FinPayeeApi.findAllActive().then(({ data }) => {
        const sortedData = data.sort((a, b) => a.name.localeCompare(b.name));
        const ssuId = mainLedgerEvents?.[0].ssuId;
        const studyType = mainLedgerEvents?.[0]?.finStudyType;
        const localStoragePayee = payeeLocalStorage[ssuId];
        const payee = localStoragePayee
          ? sortedData.find(data => data.id === localStoragePayee?.id)
          : sortedData.find(el => (studyType ? el.finStudyType === studyType : el.finStudyType === 'Elligo Managed')) ||
            sortedData[0];
        setPayees(sortedData);
        setSelectedPayee(payee);
      });
      if (fmsStudy) {
        setRemit(REMIT.fms);
        setSelectedContactName(CONTACT_NAMES.find(el => el.name === 'FMS Payments'));
      }
    }
  }, [
    createInvoiceItems.createdInvoiceDetails,
    createInvoiceItems.creditMemoLedgerEvents,
    createInvoiceItems.debitMemoLedgerEvents,
    createInvoiceItems.originalLedgerEvents,
    createInvoiceItems.pdf,
    createInvoiceItems.withholdingLedgerEvents,
    editPDFMode,
    fmsStudy,
    mainLedgerEvents,
    payeeLocalStorage,
    setPayees,
    setRemit,
    setSelectedContactName,
    setSelectedPayee
  ]);

  useEffect(() => {
    if (!isEmpty(createInvoiceItems.createdInvoiceDetails) && !isEmpty(createInvoiceItems.pdf)) {
      FinPayeeApi.findAllActive().then(({ data }) => {
        const sortedData = data.sort((a, b) => a.name.localeCompare(b.name));
        const oldPayee = parseSerializedObject(createInvoiceItems.createdInvoiceDetails?.payee);
        const isPayeeWasUpdated = !some(sortedData, el => isEqual(el, oldPayee));
        const newPayees =
          isPayeeWasUpdated && !isEmpty(oldPayee) ? [...sortedData, addSuffixToId(oldPayee)] : sortedData;
        const newSelectedPayee = newPayees.find(
          el => el.id === (isPayeeWasUpdated ? oldPayee?.id + 'old' : oldPayee?.id)
        );
        setSelectedPayee(newSelectedPayee);
        setPayees(newPayees);
      }, onRequestError);

      setRemit(createInvoiceItems.createdInvoiceDetails.remit);
      setSelectedContactName(
        CONTACT_NAMES.find(el => el.name === createInvoiceItems.createdInvoiceDetails.contactName)
      );
      setSelectedTerms(TERMS.find(el => el.name === createInvoiceItems.createdInvoiceDetails.terms));
    }
  }, [
    createInvoiceItems.createdInvoiceDetails,
    createInvoiceItems.pdf,
    setPayees,
    setRemit,
    setSelectedContactName,
    setSelectedPayee,
    setSelectedTerms
  ]);

  const disableInputs = useMemo(() => (invoiceCreated || !!savingType) && !editPDFMode, [
    editPDFMode,
    invoiceCreated,
    savingType
  ]);
  return (
    <>
      <div className="general-header-wrapper">
        <Select
          className="create-invoice-dropdown"
          label="Bill to"
          dataSource={billToList}
          onChange={newValue => !isEqual(newValue, selectedBillTo) && setSelectedBillTo(newValue)}
          value={selectedBillTo}
          data-testid="bill-to-selector"
          clearable={false}
          searchable={true}
          validate={false}
          disabled={disableInputs}
        />
        <Select
          className="create-invoice-dropdown"
          label="Payee"
          dataSource={payees}
          onChange={newValue => !isEqual(newValue, selectedPayee) && setSelectedPayee(newValue)}
          value={selectedPayee}
          customSelectedValueTemplateFunction={payees => transformedPayees(payees)}
          customOptionTemplateFunction={selectedPayee => payeeToString(selectedPayee)}
          data-testid="payee-selector"
          clearable={false}
          validate={false}
          disabled={disableInputs}
        />
        {createInvoiceItems?.createdInvoiceDetails && <InvoiceDatePicker disabled={disableInputs} />}
      </div>
      <div className="general-header-wrapper">
        <Select
          className="create-invoice-dropdown terms-dropdown"
          label="Terms"
          dataSource={TERMS}
          onChange={newValue => !isEqual(newValue, selectedPayee) && setSelectedTerms(newValue)}
          value={selectedTerms}
          data-testid="terms-selector"
          clearable={false}
          validate={false}
          disabled={disableInputs}
        />
        <Input
          className="create-invoice-input"
          label="Remit"
          value={remit}
          onChange={({ target: { value } }) => setRemit(value)}
          validate={false}
          disabled={disableInputs}
        />
        <Select
          className="create-invoice-dropdown"
          label="Contact Name"
          dataSource={CONTACT_NAMES}
          onChange={newValue => !isEqual(newValue, selectedContactName) && setSelectedContactName(newValue)}
          value={selectedContactName}
          data-testid="contact-name-selector"
          clearable={false}
          validate={false}
          disabled={disableInputs}
        />
        <Select
          className="create-invoice-dropdown"
          label="Investigator"
          dataSource={investigators}
          onChange={newValue => !isEqual(newValue, selectedInvestigator) && setSelectedInvestigator(newValue)}
          value={selectedInvestigator}
          data-testid="investigator-selector"
          clearable={true}
          validate={false}
          disabled={disableInputs}
        />
      </div>
    </>
  );
};
